import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { formatTimestamp } from "../format";
// Registers <ds-language>, the English/Japanese switch in the header.
import "@soracom/soracom-design-system/ds-language/index.mjs";
import { type Locale, normaliseLocale } from "./locale";
import { MESSAGES, type MessageKey } from "./messages";
import {
  type Count,
  createCounter,
  createTranslator,
  type Translate,
} from "./translate";

export interface I18nValue {
  readonly locale: Locale;
  readonly t: Translate<MessageKey>;
  readonly count: Count<MessageKey>;
  readonly formatTimestamp: (iso: string) => string;
}

function valueFor(locale: Locale): I18nValue {
  const t = createTranslator(MESSAGES[locale]);
  return {
    locale,
    t,
    count: createCounter(locale, t),
    formatTimestamp: (iso) => formatTimestamp(iso, locale),
  };
}

/**
 * Japanese anywhere no provider was set up. The screens were written in
 * Japanese first, and a component rendered on its own — in a test — keeps
 * reading the way it always has.
 */
const I18nContext = createContext<I18nValue>(valueFor("ja"));

/** The `<ds-language>` event, fired when the reader picks a language. */
const LANGUAGE_CHANGE_EVENT = "ds-language-change";

/**
 * Holds the reader's language and keeps `<html lang>` in step with it.
 *
 * `<ds-language>` owns the choice — it writes the cookie and announces the
 * change — so this follows its event rather than keeping a second copy of the
 * preference. Listening on the document is what lets the provider sit above
 * the shell that renders the switcher.
 */
export function I18nProvider({
  initialLocale,
  children,
}: {
  initialLocale: Locale;
  children: ReactNode;
}) {
  const [locale, setLocale] = useState(initialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = MESSAGES[locale]["app.title"];
  }, [locale]);

  useEffect(() => {
    const follow = (event: Event) => {
      const chosen = normaliseLocale(
        (event as CustomEvent<{ value?: string }>).detail?.value,
      );
      if (chosen !== null) {
        setLocale(chosen);
        dropLanguageQuery();
      }
    };
    document.addEventListener(LANGUAGE_CHANGE_EVENT, follow);
    return () => document.removeEventListener(LANGUAGE_CHANGE_EVENT, follow);
  }, []);

  const value = useMemo(() => valueFor(locale), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * `?lang=` outranks the cookie, so leaving it in the address after the reader
 * picks another language would undo the choice on the next reload. The
 * router's own history state is kept as it is.
 */
function dropLanguageQuery(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("lang")) {
    return;
  }
  url.searchParams.delete("lang");
  window.history.replaceState(window.history.state, "", url);
}

export function useI18n(): I18nValue {
  return useContext(I18nContext);
}

/**
 * `<ds-language>`, without the page reload.
 *
 * Its options are real links to `?lang=…` so they work without script; the
 * component applies the choice and then lets the link navigate. Here the
 * provider already re-renders on the change event, so a plain click is kept
 * in the page. A modified click (new tab, new window) still follows the link.
 */
export function LanguageSwitcher() {
  const { locale, t } = useI18n();
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }
    const stayInPage = (event: MouseEvent) => {
      const isPlainClick =
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey;
      const option =
        event.target instanceof Element
          ? event.target.closest("a[data-language]")
          : null;
      if (isPlainClick && option !== null) {
        event.preventDefault();
      }
    };
    element.addEventListener("click", stayInPage);
    return () => element.removeEventListener("click", stayInPage);
  }, []);

  return (
    <ds-language ref={ref} value={locale} aria-label={t("app.language")} />
  );
}
